import { Box, Text, Flex, ActionIcon, Button, Tooltip, Group, Paper, Badge, Loader, Menu } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faDownload, faCopy, faQrcode, faLink, faFileSvg, faFilePng, faImage, faArrowUpRightFromSquare, faArrowRightFromBracket, faChartLine, faChevronDown, faFileImage, faChartBar } from '@fortawesome/pro-regular-svg-icons';
import { getRelativeTime } from '@/utils/DateUtility';
import { useClipboard } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useTagsStore } from '@/stores/useTagsStore';
import { useRef, useState, useEffect } from 'react';
import { appendQrParameter } from '@/utils/LinkUtility';
import html2canvas from 'html2canvas';
import { QRCodeService } from '@/utils/qrcode/service';
import { AnalyticsQRService } from '@/services/AnalyticsQRService';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { numberToUnit } from '@/utils/StringUtility';
import { QRCode } from '@/utils/qrcode';
import { Link } from 'react-router-dom';
import { useQRCode } from '@/hooks/useQRCode';
import { SOCIAL_MEDIA_TYPES } from '@/constants/Constants';

export function QRCodeCard({ qrCode, onEdit, onDelete }: { qrCode: any, onEdit: () => void, onDelete: () => void }) {
    const clipboard = useClipboard();
    const [tags] = useTagsStore((state) => [state.tags]);
    const qrCodeRef = useRef<HTMLDivElement>(null);
    const [downloadingSvg, setDownloadingSvg] = useState(false);
    const [downloadingPng, setDownloadingPng] = useState(false);
    const [downloadingJpeg, setDownloadingJpeg] = useState(false);
    const [downloadingWebp, setDownloadingWebp] = useState(false);
    const [qrScans, setQrScans] = useState<number | null>(null);
    const [loadingScans, setLoadingScans] = useState(false);
    const [defaultWorkspace] = useWorkspaceStore(state => [state.defaultWorkspace]);

    const qrValue = appendQrParameter(qrCode.source_type === 'bio' ?
        `${qrCode.bio_link?.domain_attributes?.url || 'http://localhost:3001/'}${qrCode.bio_link?.short_url}` :
        `${qrCode.link?.domain_attributes?.url || 'http://localhost:3001/'}${qrCode.link?.short_url}` || "");

    // Helper function to get QR code type information
    const getQRTypeInfo = () => {
        // For social media QR codes (source_type is 'new')
        if (qrCode.source_type === 'new' && qrCode.social_media_type) {
            // Find the social media type in the SOCIAL_MEDIA_TYPES array
            const socialMediaType = SOCIAL_MEDIA_TYPES.find(type => type.value === qrCode.social_media_type);
            if (socialMediaType) {
                return {
                    icon: socialMediaType.icon,
                    label: socialMediaType.label,
                    color: socialMediaType.color || '#16a34a'
                };
            }
        }

        // For standard QR code types
        if (qrCode.source_type === 'bio') {
            return {
                icon: faLink,
                label: 'Bio Link',
                color: '#3366ff'
            };
        } else if (qrCode.source_type === 'link') {
            return {
                icon: faArrowUpRightFromSquare,
                label: 'Shortened Link',
                color: '#0ea5e9'
            };
        }

        // Default fallback
        return {
            icon: faQrcode,
            label: 'Website/URL',
            color: '#16a34a'
        };
    };

    // Get the QR code type information
    const qrTypeInfo = getQRTypeInfo();

    // Set a fixed size for QR codes
    const qrSize = 190;

    const qrConfig = {
        value: qrValue,
        width: qrSize,
        height: qrSize,
        margin: 0,
        dotsOptions: {
            color: qrCode.shape_color || '#000000',
            type: qrCode.shape_style || 'square' as 'square' | 'dots' | 'rounded' | 'classy' | 'classy-rounded' | 'extra-rounded',
            backgroundColor: qrCode.shape_background_color || '#FFFFFF'
        },
        cornersSquareOptions: {
            color: qrCode.border_color || '#000000',
            type: qrCode.border_style || 'square' as 'square' | 'dot' | 'extra-rounded' | 'rounded' | 'dots' | 'classy' | 'classy-rounded',
            backgroundColor: qrCode.border_background_color || '#FFFFFF'
        },
        cornersDotOptions: {
            color: qrCode.center_color || '#000000',
            type: qrCode.center_style || 'square' as 'square' | 'dot' | 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded',
            backgroundColor: qrCode.center_background_color || '#FFFFFF'
        },
        backgroundOptions: {
            color: qrCode.shape_background_color || '#FFFFFF'
        },
        image: qrCode.logo_image || undefined,
        imageOptions: qrCode.logo_image ? {
            hideBackgroundDots: true,
            imageSize: 0.4,
            width: qrCode.logo_width || 60,
            height: qrCode.logo_height || 60,
            margin: qrCode.logo_margin || 5,
            crossOrigin: "anonymous"
        } : undefined,
        frameType: 'none' as 'none',
        frameColor: '#000000',
        frameText: '',
        frameTextColor: '#FFFFFF'
    };

    const { downloadQR } = useQRCode(qrConfig);

    const downloadQRWithFrame = async (options: { name: string, extension: string, quality: number }) => {
        // Set loading state based on file type
        if (options.extension === 'svg') {
            setDownloadingSvg(true);
        } else if (options.extension === 'png') {
            setDownloadingPng(true);
        } else if (options.extension === 'jpeg') {
            setDownloadingJpeg(true);
        } else if (options.extension === 'webp') {
            setDownloadingWebp(true);
        }

        if (!qrCodeRef.current) {
            console.error('QR code reference not found');
            setDownloadingSvg(false);
            setDownloadingPng(false);
            setDownloadingJpeg(false);
            setDownloadingWebp(false);
            return;
        }

        try {
            // Check if the QR code has frame data
            const hasFrameData = qrCode.frame_type && qrCode.frame_type !== 'none';

            if (hasFrameData) {
                try {
                    // Create a temporary div for the QR code
                    const tempDiv = document.createElement('div');
                    tempDiv.style.position = 'fixed';
                    tempDiv.style.left = '-9999px';
                    tempDiv.style.top = '-9999px';
                    tempDiv.style.width = `${qrSize + 80}px`; // Add extra space for the frame
                    tempDiv.style.height = `${qrSize + 80}px`;
                    tempDiv.style.backgroundColor = '#FFFFFF';
                    tempDiv.style.padding = '20px';
                    document.body.appendChild(tempDiv);

                    // Create a frame container with styles that match QRCodeFrame component
                    const frameContainer = document.createElement('div');
                    
                    // Base frame styles
                    frameContainer.style.padding = '10px'; // Match the padding from QRCodeFrame
                    frameContainer.style.border = `2px solid ${qrCode.frame_color || '#000000'}`;
                    frameContainer.style.backgroundColor = qrCode.frame_color || '#000000';
                    frameContainer.style.display = 'flex';
                    frameContainer.style.flexDirection = 'column';
                    frameContainer.style.alignItems = 'center';
                    frameContainer.style.justifyContent = 'center';
                    frameContainer.style.boxSizing = 'border-box';
                    
                    // Apply specific styles based on frame type
                    switch (qrCode.frame_type) {
                        case 'square':
                            frameContainer.style.borderRadius = '0px';
                            break;
                        case 'rounded':
                            frameContainer.style.borderRadius = '16px'; // Match the exact value from QRCodeFrame
                            break;
                        case 'phone':
                            frameContainer.style.padding = '20px 12px 30px'; // Match the exact padding from QRCodeFrame
                            frameContainer.style.borderRadius = '24px';
                            frameContainer.style.position = 'relative';
                            
                            // Add phone notch element
                            const phoneNotch = document.createElement('div');
                            phoneNotch.style.position = 'absolute';
                            phoneNotch.style.top = '8px';
                            phoneNotch.style.width = '60px';
                            phoneNotch.style.height = '6px';
                            phoneNotch.style.backgroundColor = '#333';
                            phoneNotch.style.borderRadius = '3px';
                            frameContainer.appendChild(phoneNotch);
                            break;
                        case 'tablet':
                            frameContainer.style.padding = '10px 10px 50px'; // Match the exact padding from QRCodeFrame
                            frameContainer.style.borderRadius = '12px';
                            frameContainer.style.position = 'relative';
                            
                            // Add tablet button element
                            const tabletButton = document.createElement('div');
                            tabletButton.style.position = 'absolute';
                            tabletButton.style.bottom = '35px';
                            tabletButton.style.left = '50%';
                            tabletButton.style.transform = 'translateX(-50%)';
                            tabletButton.style.width = '20%';
                            tabletButton.style.height = '4px';
                            tabletButton.style.backgroundColor = '#333';
                            tabletButton.style.borderRadius = '2px';
                            frameContainer.appendChild(tabletButton);
                            break;
                        case 'circle':
                            frameContainer.style.borderRadius = '50%';
                            frameContainer.style.aspectRatio = '1 / 1';
                            frameContainer.style.padding = '12px';
                            frameContainer.style.position = 'relative';
                            frameContainer.style.paddingBottom = qrCode.frame_text ? '40px' : '12px'; // Match the exact padding from QRCodeFrame
                            break;
                        case 'clipboard':
                            frameContainer.style.padding = '25px 12px 12px'; // Match the exact padding from QRCodeFrame
                            frameContainer.style.borderRadius = '8px 8px 4px 4px'; // Match the exact border radius from QRCodeFrame
                            frameContainer.style.position = 'relative';
                            
                            // Add clipboard clip element
                            const clipboardClip = document.createElement('div');
                            clipboardClip.style.position = 'absolute';
                            clipboardClip.style.top = '-10px';
                            clipboardClip.style.left = '50%';
                            clipboardClip.style.transform = 'translateX(-50%)';
                            clipboardClip.style.width = '30%';
                            clipboardClip.style.height = '20px';
                            clipboardClip.style.backgroundColor = qrCode.frame_color || '#000000';
                            clipboardClip.style.borderRadius = '10px 10px 0 0';
                            clipboardClip.style.border = `2px solid ${qrCode.frame_color || '#000000'}`;
                            clipboardClip.style.borderBottom = 'none';
                            frameContainer.appendChild(clipboardClip);
                            break;
                    }
                    
                    tempDiv.appendChild(frameContainer);

                    // Create QR code container with white background
                    const qrContainer = document.createElement('div');
                    qrContainer.style.backgroundColor = '#FFFFFF';
                    qrContainer.style.padding = '4px'; // Match the padding from QRCodeFrame
                    qrContainer.style.paddingLeft = '6px';  // Add padding on left
                    qrContainer.style.paddingRight = '0px'; // No padding on right
                    qrContainer.style.paddingTop = '1px';   // Minimal padding on top
                    qrContainer.style.paddingBottom = '1px'; // Minimal padding on bottom
                    
                    // Set border radius based on frame type to match QRCodeFrame
                    qrContainer.style.borderRadius = qrCode.frame_type === 'circle' ? '50%' : 
                                                    qrCode.frame_type === 'square' ? '0px' : '4px';
                    
                    qrContainer.style.display = 'flex';
                    qrContainer.style.justifyContent = 'flex-start'; // Align to the left instead of center
                    qrContainer.style.alignItems = 'center';
                    
                    // Set width and height based on frame type to match QRCodeFrame
                    if (qrCode.frame_type === 'circle') {
                        qrContainer.style.width = '90%';
                        qrContainer.style.height = '90%';
                    } else if (qrCode.frame_type === 'phone' || qrCode.frame_type === 'tablet') {
                        qrContainer.style.width = '95%';
                        qrContainer.style.height = '95%';
                        qrContainer.style.marginTop = qrCode.frame_type === 'phone' ? '2px' : '0px';
                        qrContainer.style.marginBottom = qrCode.frame_type === 'tablet' ? '5px' : '0px';
                    } else {
                        qrContainer.style.width = '95%';
                        qrContainer.style.height = '95%';
                    }
                    
                    frameContainer.appendChild(qrContainer);

                    // Create QR code element
                    const qrCodeElement = document.createElement('div');
                    qrCodeElement.style.width = '100%';
                    qrCodeElement.style.height = '100%';
                    qrContainer.appendChild(qrCodeElement);
                    
                    // Use the QR code service to render the QR code
                    const qrService = new QRCodeService({
                        ...qrConfig,
                        width: qrCode.frame_type === 'circle' ? qrSize - 15 : 
                               qrCode.frame_type === 'phone' || qrCode.frame_type === 'tablet' ? qrSize - 3 : 
                               qrSize - 3, // Make QR code even larger
                        height: qrCode.frame_type === 'circle' ? qrSize - 15 : 
                                qrCode.frame_type === 'phone' || qrCode.frame_type === 'tablet' ? qrSize - 3 : 
                                qrSize - 3,
                        margin: 0 // Remove margin from QR code
                    });
                    qrService.appendToElement(qrCodeElement);
                    
                    // Add frame text if present
                    if (qrCode.frame_text) {
                        const textElement = document.createElement('div');
                        textElement.style.color = qrCode.frame_text_color || '#FFFFFF';
                        textElement.style.fontWeight = 'bold';
                        textElement.style.marginTop = '4px'; // Reduce margin
                        textElement.style.fontSize = qrCode.frame_text.length > 10 ? '12px' : 
                                                    qrCode.frame_text.length > 7 ? '13px' : '14px';
                        textElement.style.textAlign = 'center';
                        textElement.style.maxWidth = '100%';
                        textElement.style.overflow = 'hidden';
                        textElement.style.textOverflow = 'ellipsis';
                        textElement.style.whiteSpace = 'nowrap';
                        
                        // Position text differently based on frame type
                        if (qrCode.frame_type === 'tablet') {
                            textElement.style.position = 'absolute';
                            textElement.style.bottom = '10px';
                            textElement.style.left = '50%';
                            textElement.style.transform = 'translateX(-50%)';
                            textElement.style.width = '100%';
                        } else if (qrCode.frame_type === 'circle') {
                            textElement.style.position = 'absolute';
                            textElement.style.bottom = '10px';
                            textElement.style.left = '50%';
                            textElement.style.transform = 'translateX(-50%)';
                            textElement.style.width = '100%';
                            textElement.style.textAlign = 'center';
                            textElement.style.fontSize = '14px';
                            textElement.style.fontWeight = 'bold';
                        }
                        
                        textElement.innerText = qrCode.frame_text;
                        
                        // Only add text if not tablet or circle (these are added in special positions)
                        if (qrCode.frame_type !== 'tablet' && qrCode.frame_type !== 'circle') {
                            frameContainer.appendChild(textElement);
                        }
                    }

                    // Wait for the QR code to render
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    // Use html2canvas to capture the frame container
                    const canvas = await html2canvas(frameContainer, {
                        backgroundColor: null,
                        scale: 1.3, // Change scale to 1.5 for better quality
                        logging: true, // Enable logging for debugging
                        useCORS: true,
                        allowTaint: true
                    });

                    // Convert to data URL and download
                    let downloadUrl;
                    if (options.extension === 'svg') {
                        // For SVG, create a new SVG that includes everything
                        const svgData = `
                            <svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}">
                                <image href="${canvas.toDataURL('image/png', options.quality)}" width="${canvas.width}" height="${canvas.height}" />
                            </svg>
                        `;
                        const blob = new Blob([svgData], { type: 'image/svg+xml' });
                        downloadUrl = URL.createObjectURL(blob);
                    } else {
                        // For other formats, use canvas toDataURL
                        const mimeType = options.extension === 'jpeg' ? 'image/jpeg' :
                            options.extension === 'webp' ? 'image/webp' : 'image/png';
                        downloadUrl = canvas.toDataURL(mimeType, options.quality);
                    }

                    // Create download link
                    const link = document.createElement('a');
                    link.download = `${options.name}.${options.extension}`;
                    link.href = downloadUrl;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // Clean up
                    document.body.removeChild(tempDiv);
                    if (options.extension === 'svg') {
                        URL.revokeObjectURL(downloadUrl);
                    }
                } catch (error) {
                    console.error('Error downloading QR code with frame:', error);
                    notifications.show({
                        color: 'red',
                        message: 'Failed to download QR code',
                    });
                } finally {
                    // Reset loading state
                    if (options.extension === 'svg') {
                        setDownloadingSvg(false);
                    } else if (options.extension === 'png') {
                        setDownloadingPng(false);
                    } else if (options.extension === 'jpeg') {
                        setDownloadingJpeg(false);
                    } else if (options.extension === 'webp') {
                        setDownloadingWebp(false);
                    }
                }
            } else {
                // If no frame data, use the original implementation
                // Get the QR code element
                const qrElement = qrCodeRef.current;

                // Use html2canvas to capture the QR code with its frame
                const canvas = await html2canvas(qrElement as HTMLElement, {
                    backgroundColor: null, // Transparent background
                    scale: 1.3, // Higher scale for better quality
                    logging: false,
                    useCORS: true, // Enable CORS for images
                    allowTaint: true, // Allow tainted canvas if needed
                    width: qrElement.offsetWidth,
                    height: qrElement.offsetHeight
                });

                // Trim white space from the canvas
                const trimmedCanvas = document.createElement('canvas');
                const trimmedCtx = trimmedCanvas.getContext('2d');

                if (!trimmedCtx) {
                    throw new Error('Could not get canvas context');
                }

                // Get the image data to find non-transparent pixels
                const imageData = canvas.getContext('2d')?.getImageData(0, 0, canvas.width, canvas.height);

                if (!imageData) {
                    throw new Error('Could not get image data');
                }

                // Find the bounds of non-transparent pixels
                let minX = canvas.width;
                let minY = canvas.height;
                let maxX = 0;
                let maxY = 0;

                const data = imageData.data;
                for (let y = 0; y < canvas.height; y++) {
                    for (let x = 0; x < canvas.width; x++) {
                        const alpha = data[((y * canvas.width + x) * 4) + 3];
                        if (alpha > 0) {
                            minX = Math.min(minX, x);
                            minY = Math.min(minY, y);
                            maxX = Math.max(maxX, x);
                            maxY = Math.max(maxY, y);
                        }
                    }
                }

                // Add a small padding
                const padding = 5;
                minX = Math.max(0, minX - padding);
                minY = Math.max(0, minY - padding);
                maxX = Math.min(canvas.width, maxX + padding);
                maxY = Math.min(canvas.height, maxY + padding);

                // Set the trimmed canvas size
                const width = maxX - minX;
                const height = maxY - minY;

                // Ensure we have valid dimensions
                if (width <= 0 || height <= 0) {
                    throw new Error('Invalid dimensions after trimming');
                }

                trimmedCanvas.width = width;
                trimmedCanvas.height = height;

                // Draw the trimmed image
                trimmedCtx.drawImage(
                    canvas,
                    minX, minY, width, height,
                    0, 0, width, height
                );

                // Convert to data URL and download
                let downloadUrl;
                if (options.extension === 'svg') {
                    // For SVG, create a new SVG that includes everything
                    const svgData = `
                        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
                            <image href="${trimmedCanvas.toDataURL('image/png', options.quality)}" width="${width}" height="${height}" />
                        </svg>
                    `;
                    const blob = new Blob([svgData], { type: 'image/svg+xml' });
                    downloadUrl = URL.createObjectURL(blob);
                } else {
                    // For other formats, use canvas toDataURL
                    const mimeType = options.extension === 'jpeg' ? 'image/jpeg' :
                        options.extension === 'webp' ? 'image/webp' : 'image/png';
                    downloadUrl = trimmedCanvas.toDataURL(mimeType, options.quality);
                }

                // Create download link
                const link = document.createElement('a');
                link.download = `${options.name}.${options.extension}`;
                link.href = downloadUrl;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up
                if (options.extension === 'svg') {
                    URL.revokeObjectURL(downloadUrl);
                }
            }
        } catch (error) {
            console.error('Error downloading QR code with frame:', error);
            notifications.show({
                color: 'red',
                message: 'Failed to download QR code',
            });
        } finally {
            // Reset loading state
            if (options.extension === 'svg') {
                setDownloadingSvg(false);
            } else if (options.extension === 'png') {
                setDownloadingPng(false);
            } else if (options.extension === 'jpeg') {
                setDownloadingJpeg(false);
            } else if (options.extension === 'webp') {
                setDownloadingWebp(false);
            }
        }
    };

    const truncateUrl = (url: string, maxLength: number = 40) => {
        const cleanUrl = url.replace(/^https?:\/\//, '');
        if (cleanUrl.length <= maxLength) return cleanUrl;

        const halfLength = Math.floor(maxLength / 2);
        const start = cleanUrl.substring(0, halfLength);
        const end = cleanUrl.substring(cleanUrl.length - halfLength);
        return `${start}...`;
    };

    // Function to find the tag name from the tags store
    const findTagName = (tagId: string) => {
        const tag = tags.find((t: any) => t._id === tagId);
        // @ts-ignore
        return tag?.name || tagId;
    };

    // Fetch QR scan statistics
    useEffect(() => {
        const fetchQRScans = async () => {
            if (!qrCode?._id || !defaultWorkspace?._id) return;
            
            setLoadingScans(true);
            try {
                // Get all-time data by using the workspace creation date as the start date
                // If workspace creation date is not available, use a very old date (e.g., 5 years ago)
                const fromDate = defaultWorkspace?.created_at 
                    ? new Date(defaultWorkspace.created_at) 
                    : new Date(new Date().setFullYear(new Date().getFullYear() - 5));
                const toDate = new Date();
                
                const fromDateStr = fromDate.toISOString();
                const toDateStr = toDate.toISOString();
                
                // Get the link_id from the QR code object
                // The Analytics API expects the link_id parameter, not the QR code ID directly
                const linkId = qrCode.link_id || qrCode.bio_link_id;
                
                console.log('Fetching QR scans with linkId:', linkId);
                
                const response = await new AnalyticsQRService().overview(
                    defaultWorkspace._id,
                    linkId,
                    fromDateStr,
                    toDateStr
                );
                
                if (response?.data?.status && response?.data?.data?.current) {
                    console.log('QR scan data:', response.data.data);
                    setQrScans(response.data.data.current.qr_scans);
                } else {
                    console.log('No QR scan data found');
                    setQrScans(0);
                }
            } catch (error) {
                console.error('Error fetching QR scan statistics:', error);
                setQrScans(0);
            } finally {
                setLoadingScans(false);
            }
        };
        
        fetchQRScans();
    }, [qrCode._id, defaultWorkspace?._id]);

    return (
        <Paper
            w={500}
            shadow="sm"
            radius="lg"
            withBorder
            style={{
                overflow: 'hidden',
                transition: 'transform 0.2s, box-shadow 0.2s',
                ':hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                }
            }}
        >
            <Flex style={{ minHeight: 200 }}>
                {/* Left side - QR Code */}
                <Box style={{
                    width: qrSize + 30,
                    minWidth: qrSize + 30,
                    background: '#f2f6f7',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    padding: '20px',
                    borderRadius: '8px'
                }}>
                    <Box
                        ref={qrCodeRef}
                        style={{
                            position: 'relative',
                            width: `${qrSize}px`,
                            height: `${qrSize}px`,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'hidden',
                            backgroundColor: qrCode.shape_background_color || '#FFFFFF',
                            borderRadius: '8px'
                        }}>
                        <QRCode
                            {...qrConfig}
                            width={qrSize}
                            height={qrSize}
                        />
                    </Box>

                    {/* Action Buttons */}
                    <Group gap={8} mt="md" style={{ position: 'relative' }}>
                        <Tooltip label="Edit">
                            <ActionIcon
                                variant="filled"
                                color="blue"
                                onClick={onEdit}
                                style={{ backgroundColor: 'rgba(231, 245, 255, 0.9)', color: '#1c7ed6' }}
                            >
                                <FontAwesomeIcon icon={faEdit} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                            <ActionIcon
                                variant="filled"
                                color="red"
                                onClick={onDelete}
                                style={{ backgroundColor: 'rgba(255, 245, 245, 0.9)', color: '#fa5252' }}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Detailed Stats">
                            <Link to={`/analytics/qr-links?link_id=${qrCode.link_id || qrCode.bio_link_id}`} style={{ textDecoration: 'none' }}>
                                <ActionIcon
                                    variant="filled"
                                    color="green"
                                    style={{ backgroundColor: 'rgba(233, 249, 238, 0.9)', color: '#40c057' }}
                                >
                                    <FontAwesomeIcon icon={faChartLine} />
                                </ActionIcon>
                            </Link>
                        </Tooltip>
                        <Tooltip label="Download">
                            <Menu position="bottom-end" shadow="md">
                                <Menu.Target>
                                    <ActionIcon
                                        variant="filled"
                                        color="gray"
                                        style={{
                                            backgroundColor: 'rgba(241, 243, 245, 0.9)',
                                            color: '#495057'
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faDownload} />
                                    </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Label>Download Options</Menu.Label>
                                    <Menu.Item
                                        leftSection={<FontAwesomeIcon icon={faFileSvg} />}
                                        disabled={downloadingSvg}
                                        onClick={() => {
                                            if (!downloadingSvg) {
                                                downloadQRWithFrame({
                                                    name: qrCode.title || 'qr-code',
                                                    extension: 'svg',
                                                    quality: 1
                                                });
                                            }
                                        }}
                                    >
                                        {downloadingSvg ? 'Downloading SVG...' : 'SVG Format'}
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<FontAwesomeIcon icon={faFilePng} />}
                                        disabled={downloadingPng}
                                        onClick={() => {
                                            if (!downloadingPng) {
                                                downloadQRWithFrame({
                                                    name: qrCode.title || 'qr-code',
                                                    extension: 'png',
                                                    quality: 1
                                                });
                                            }
                                        }}
                                    >
                                        {downloadingPng ? 'Downloading PNG...' : 'PNG Format'}
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<FontAwesomeIcon icon={faFileImage} />}
                                        disabled={downloadingJpeg}
                                        onClick={() => {
                                            if (!downloadingJpeg) {
                                                downloadQRWithFrame({
                                                    name: qrCode.title || 'qr-code',
                                                    extension: 'jpeg',
                                                    quality: 1
                                                });
                                            }
                                        }}
                                    >
                                        {downloadingJpeg ? 'Downloading JPEG...' : 'JPEG Format'}
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<FontAwesomeIcon icon={faFileImage} />}
                                        disabled={downloadingWebp}
                                        onClick={() => {
                                            if (!downloadingWebp) {
                                                downloadQRWithFrame({
                                                    name: qrCode.title || 'qr-code',
                                                    extension: 'webp',
                                                    quality: 1
                                                });
                                            }
                                        }}
                                    >
                                        {downloadingWebp ? 'Downloading WEBP...' : 'WEBP Format'}
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        </Tooltip>
                    </Group>
                </Box>

                {/* Right side - Details */}
                <Box style={{ flex: 1, background: '#ffffff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} p="xl">
                    <Flex align="center" gap={6}>
                        <FontAwesomeIcon
                            icon={qrTypeInfo.icon}
                            style={{
                                width: 15,
                                height: 15,
                                color: qrTypeInfo.color
                            }}
                        />
                        <Text
                            size="xs"
                            style={{
                                color: qrTypeInfo.color,
                                fontWeight: 500
                            }}
                        >
                            {qrTypeInfo.label}
                        </Text>
                    </Flex>

                    <Text size="xl" fw={600} c="#003aa9" lineClamp={1} style={{ lineHeight: 1.3, wordBreak: 'break-word' }} mt={15} mb="xs">
                        {qrCode.title || 'Untitled'}
                    </Text>

                    <Text size="sm" c="dimmed" lineClamp={1} style={{ lineHeight: 1.5 }}>
                        {qrCode.description || 'No description'}
                    </Text>

                    {/* Show link information based on source type */}
                    {(qrCode.link || qrCode.bio_link) && (
                        <Box mb="md" style={{ width: '100%' }}>
                            {/* Shortened URL with QR icon and Copy Button */}
                            <Flex align="center" mt={15} style={{ width: '100%' }} mb={-4}>
                                <Box mr="xs" style={{ color: '#003aa9', width: 16, flexShrink: 0 }}>
                                    <FontAwesomeIcon icon={faLink} size="sm" />
                                </Box>
                                <Text size="xs" fw={500} style={{ flexGrow: 1, minWidth: 0 }} lineClamp={1}>
                                    {qrCode.source_type === 'bio' ?
                                        truncateUrl(`${qrCode.bio_link?.domain_attributes?.url || 'http://localhost:3001/'}${qrCode.bio_link?.short_url}`, 35) :
                                        truncateUrl(`${qrCode.link?.domain_attributes?.url || 'http://localhost:3001/'}${qrCode.link?.short_url}`, 35)
                                    }
                                </Text>
                                <Box ml="xs" style={{ flexShrink: 0 }}>
                                    <Tooltip label="Copy link">
                                        <ActionIcon
                                            variant="subtle"
                                            color="blue"
                                            size="sm"
                                            onClick={() => {
                                                const link = qrCode.source_type === 'bio' ?
                                                    `${qrCode.bio_link?.domain_attributes?.url || 'http://localhost:3001/'}${qrCode.bio_link?.short_url}` :
                                                    `${qrCode.link?.domain_attributes?.url || 'http://localhost:3001/'}${qrCode.link?.short_url}`;
                                                clipboard.copy(link);
                                                notifications.show({
                                                    color: 'green',
                                                    message: 'Link copied to clipboard',
                                                });
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faCopy} size="sm" />
                                        </ActionIcon>
                                    </Tooltip>
                                </Box>
                            </Flex>

                            {/* Destination URL with domain icon */}
                            <Flex align="center" style={{ width: '100%' }}>
                                <Box mr="xs" style={{ color: '#003aa9', width: 16, flexShrink: 0 }}>
                                    <FontAwesomeIcon icon={faArrowRightFromBracket} size="sm" />
                                </Box>
                                <Text size="xs" fw={500} style={{ flexGrow: 1, minWidth: 0 }} lineClamp={1}>
                                    {qrCode.source_type === 'bio'
                                        ? (qrCode.bio_link?.url ? truncateUrl(qrCode.bio_link.url) : 'No destination URL')
                                        : (qrCode.link?.url ? truncateUrl(qrCode.link.url) : '')}
                                </Text>
                            </Flex>

                            {/* QR Scan Statistics */}
                            <Flex align="center" style={{ width: '100%' }}>
                                <Box mr="xs" style={{ color: '#003aa9', width: 16, flexShrink: 0 }}>
                                    <FontAwesomeIcon icon={faChartBar} size="sm" />
                                </Box>
                                <Text size="xs" fw={500} style={{ flexGrow: 1, minWidth: 0 }} lineClamp={1}>
                                    {loadingScans ? (
                                        <Loader size="xs" />
                                    ) : (
                                        `${qrScans !== null ? numberToUnit(qrScans) : '0'} total scans`
                                    )}
                                </Text>
                            </Flex>
                        </Box>
                    )}

                    {/* Only show campaign for regular links */}
                    {qrCode.source_type !== 'bio' && qrCode.campaign && (
                        <Text size="xs" c="dimmed" fw={500} mb="xs">
                            Campaign: <Text span c="#003aa9" fw={600}>{qrCode.campaign.name}</Text>
                        </Text>
                    )}

                    {/* Tags Section - always show the container for consistent spacing */}
                    <Box mb="xs" style={{ maxWidth: '100%', overflow: 'hidden', minHeight: 28 }}>
                        {qrCode.source_type === 'bio' ? (
                            qrCode.bio_link?.tags && qrCode.bio_link.tags.length > 0 ? (
                                <Group gap={8} wrap="nowrap" style={{ overflow: 'hidden' }}>
                                    {qrCode.bio_link.tags.slice(0, 3).map((tag: any, index: number) => {
                                        const tagName = typeof tag === 'string' ? findTagName(tag) : tag.name;
                                        return (
                                            <Paper
                                                key={index}
                                                p={6}
                                                style={{
                                                    backgroundColor: '#edf2ff',
                                                    borderRadius: '4px',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Text size="xs" c="#003aa9" fw={500}>
                                                    {tagName}
                                                </Text>
                                            </Paper>
                                        );
                                    })}
                                    {qrCode.bio_link.tags.length > 3 && (
                                        <Text size="xs" c="dimmed" fw={500} style={{ flexShrink: 0 }}>
                                            +{qrCode.bio_link.tags.length - 3} more
                                        </Text>
                                    )}
                                </Group>
                            ) : (
                                <Text size="xs" c="dimmed">No tags</Text>
                            )
                        ) : (
                            qrCode.link?.tags && qrCode.link.tags.length > 0 ? (
                                <Group gap={8} wrap="nowrap" style={{ overflow: 'hidden' }}>
                                    {qrCode.link.tags.slice(0, 3).map((tag: any, index: number) => {
                                        const tagName = typeof tag === 'string' ? findTagName(tag) : tag.name;
                                        return (
                                            <Paper
                                                key={index}
                                                p={6}
                                                style={{
                                                    backgroundColor: '#edf2ff',
                                                    borderRadius: '4px',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Text size="xs" c="#003aa9" fw={500}>
                                                    {tagName}
                                                </Text>
                                            </Paper>
                                        );
                                    })}
                                    {qrCode.link.tags.length > 3 && (
                                        <Text size="xs" c="dimmed" fw={500} style={{ flexShrink: 0 }}>
                                            +{qrCode.link.tags.length - 3} more
                                        </Text>
                                    )}
                                </Group>
                            ) : (
                                <Text size="xs" c="dimmed">No tags</Text>
                            )
                        )}
                    </Box>

                    <Text size="xs" c="dimmed" fw={500} mb="lg">
                        Created {getRelativeTime(qrCode.created_at)}
                    </Text>
                </Box>
            </Flex>
        </Paper>
    );
}